/**
 * homeworkDue — turn a homework `due_at` (ISO date or datetime) into a
 * localised, relative deadline descriptor for the Lekser board (Fase 4).
 *
 * Homework deadlines are day-granular: the meaningful unit is "which day is
 * this due". We therefore compare on the leading `yyyy-mm-dd` only, which is
 * timezone-immune (no instant arithmetic across DST) — the same discipline the
 * backend spawner uses for spawn keys.
 *
 * The descriptor is i18n-agnostic: it returns a `kind` (+ a pre-formatted nb
 * date for the generic case) and the component maps it to a `t()` key. This
 * keeps the helper pure and unit-testable without a render harness.
 */

export type DueDescriptor =
  | { kind: 'none' }
  | { kind: 'overdue' }
  | { kind: 'today' }
  | { kind: 'tomorrow' }
  | { kind: 'date'; date: string };

/** Days-since-epoch for a y/m/d triple (UTC midnight — pure date math). */
function dayNumber(y: number, m: number, d: number): number {
  return Math.floor(Date.UTC(y, m - 1, d) / 86_400_000);
}

const nbDate = new Intl.DateTimeFormat('nb-NO', {
  day: 'numeric',
  month: 'long',
  timeZone: 'UTC',
});

/**
 * Describe a homework deadline relative to `now`.
 *
 * @param dueAt ISO date ('2026-06-25') or datetime; null = no deadline.
 * @param now   the reference instant (defaults to real now; injectable for tests).
 */
export function describeDue(dueAt: string | null | undefined, now: Date = new Date()): DueDescriptor {
  if (!dueAt) return { kind: 'none' };
  const [dy, dm, dd] = dueAt.slice(0, 10).split('-').map(Number);
  if (!dy || !dm || !dd) return { kind: 'none' };

  const due = dayNumber(dy, dm, dd);
  const today = dayNumber(now.getFullYear(), now.getMonth() + 1, now.getDate());
  const delta = due - today;

  if (delta < 0) return { kind: 'overdue' };
  if (delta === 0) return { kind: 'today' };
  if (delta === 1) return { kind: 'tomorrow' };
  return { kind: 'date', date: nbDate.format(Date.UTC(dy, dm - 1, dd)) };
}

/**
 * En frist som haster: passert, i dag eller i morgen. Kortene viser den i amber
 * (prinsipp 3 i design/HANDOFF.md), og teksten sier det samme (#154).
 */
export function isUrgentDue(due: DueDescriptor): boolean {
  return due.kind === 'overdue' || due.kind === 'today' || due.kind === 'tomorrow';
}
